import {ResizeDirections, SectionMediaTypesEnum, SectionModuleTypeEnum, SectionTypesEnum} from "@/scripts/common/enums.js";
import {createLayoutPatch, LAYOUT_TYPES} from "@/scripts/common/layoutConfigurations.js";

const convertibleTypes = new Set([SectionTypesEnum.TEXT, SectionTypesEnum.MEDIA, SectionTypesEnum.TEXT_AND_MEDIA, SectionTypesEnum.GENERAL]);
const DEFAULT_TEXT = "<h3>This is your heading</h3><p>You can write here as much as you want, this text will always look nice, whether you write longer paragraphs or just a few words. Click here and try it out.</p>";

function createId() {
    return typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createSpacer(start, end) {
    return {
        moduleId: `spacer-${createId()}`,
        type: SectionModuleTypeEnum.SPACER,
        gridSettings: {columnStart: start, columnEnd: end, columnSpan: end - start, rowStart: 1, rowEnd: 2},
        resizeSettings: {minColumnSpan: 0, maxColumnSpan: 7, resizeDirections: ResizeDirections.HORIZONTAL},
    };
}

function createContentLayout(contentModules, previousModules = []) {
    const modules = [createSpacer(1, 1)];
    const layoutContent = [...contentModules].sort((left, right) =>
        (left.type === SectionModuleTypeEnum.MEDIA ? 0 : 1) - (right.type === SectionModuleTypeEnum.MEDIA ? 0 : 1)
    );
    const hasMatchingModuleTypes = layoutContent.length === 2
        && layoutContent[0].type === layoutContent[1].type;
    layoutContent.forEach((content, index) => {
        const previous = previousModules.find(module => module.moduleId === content.moduleId);
        const start = index === 0 ? 1 : hasMatchingModuleTypes ? 7 : 8;
        const end = index === 0 ? 7 : 13;
        modules.push({
            moduleId: content.moduleId,
            type: content.type,
            gridSettings: {
                ...previous?.gridSettings,
                columnStart: start,
                columnEnd: end,
                columnSpan: end - start,
                rowStart: 1,
                rowEnd: 2,
                isFullBleed: false,
            },
            resizeSettings: {
                minColumnSpan: content.type === SectionModuleTypeEnum.TEXT ? 3 : 2,
                maxColumnSpan: content.type === SectionModuleTypeEnum.TEXT ? 10 : 9,
                resizeDirections: content.type === SectionModuleTypeEnum.TEXT ? ResizeDirections.HORIZONTAL : ResizeDirections.ALL,
                autoHeight: previous?.resizeSettings?.autoHeight ?? true,
            },
        });
        if (index === 0) modules.push(createSpacer(7, hasMatchingModuleTypes ? 7 : 8));
    });
    modules.push(createSpacer(13, 13));
    return {modules, totalColumns: 12};
}

function toGeneralSection(section) {
    if (section.type === SectionTypesEnum.GENERAL) return {...section, contentModules: [...section.contentModules]};

    const layoutModules = section.layoutSettings?.modules || [];
    const contentModules = layoutModules.flatMap(module => {
        if (module.type === SectionModuleTypeEnum.TEXT && section.text && section.contentSettings?.showText !== false) {
            return [{moduleId: module.moduleId, type: module.type, text: section.text, cta: section.cta || null}];
        }
        if (module.type === SectionModuleTypeEnum.MEDIA && section.media && section.contentSettings?.showMedia !== false) {
            return [{moduleId: module.moduleId, type: module.type, media: section.media}];
        }
        return [];
    });
    const {text, media, cta, ...general} = section;
    return {
        ...general,
        type: SectionTypesEnum.GENERAL,
        contentModules,
    };
}

export function addGeneralContentModule(section, {type, media = null}) {
    const general = toGeneralSection(section);
    if (general.contentModules.length >= 2) return section;

    const contentModule = type === SectionModuleTypeEnum.TEXT
        ? {moduleId: createId(), type, text: {text: DEFAULT_TEXT, style: null}, cta: null}
        : {moduleId: createId(), type: SectionModuleTypeEnum.MEDIA, media: media || createGeneralMedia()};
    const contentModules = [...general.contentModules, contentModule];

    return {
        ...general,
        contentModules,
        contentSettings: {
            ...general.contentSettings,
            showText: contentModules.some(module => module.type === SectionModuleTypeEnum.TEXT),
            showMedia: contentModules.some(module => module.type === SectionModuleTypeEnum.MEDIA),
            flipped: false,
        },
        styleSettings: {...general.styleSettings, boxAlign: contentModules.every(module => module.type === SectionModuleTypeEnum.TEXT) ? "left-top" : "left"},
        layoutSettings: createContentLayout(contentModules, general.layoutSettings?.modules),
    };
}

export function createGeneralMedia(type = SectionMediaTypesEnum.IMAGE, mockupSettings = null) {
    return type === SectionMediaTypesEnum.IMAGE ? {
        type,
        frame: null,
        caption: null,
        displayMode: "medium",
        url: "/example_project_images/empty-image-and-text-image.jpg",
        width: 1500,
        height: 1125,
        mockupSettings,
    } : {
        type,
        frame: null,
        caption: null,
        displayMode: "large",
        url: null,
        width: null,
        height: null,
    };
}

// Resolve the canonical payload for both legacy sections and module-owned content.
export function getModuleContent(section, moduleId) {
    return section?.type === SectionTypesEnum.GENERAL
        ? section.contentModules.find(module => module.moduleId === moduleId)
        : section;
}

export function getModuleMedia(section, moduleId) {
    return getModuleContent(section, moduleId)?.media;
}

export function withModuleCta(section, moduleId, cta) {
    return moduleId
        ? {...section, contentModules: section.contentModules.map(module =>
            module.moduleId === moduleId ? {...module, cta} : module)}
        : {...section, cta};
}

export function canAddGeneralModule(section) {
    if (!convertibleTypes.has(section?.type)) return false;
    if (section.layoutSettings?.modules?.some(module => ![
        SectionModuleTypeEnum.TEXT, SectionModuleTypeEnum.MEDIA, SectionModuleTypeEnum.SPACER,
    ].includes(module.type))) return false;
    return section.type === SectionTypesEnum.GENERAL
        ? section.contentModules?.length < 2
        : section.layoutSettings?.modules?.filter(module =>
            (module.type === SectionModuleTypeEnum.TEXT && section.contentSettings?.showText !== false)
            || (module.type === SectionModuleTypeEnum.MEDIA && section.contentSettings?.showMedia !== false)
        ).length === 1;
}

export function removeGeneralModule(section, moduleId) {
    if (section.type !== SectionTypesEnum.GENERAL || section.contentModules.length < 2) return section;
    const contentModules = section.contentModules.filter(module => module.moduleId !== moduleId);
    if (contentModules.length === section.contentModules.length) return section;
    const remaining = contentModules[0];
    const isText = remaining.type === SectionModuleTypeEnum.TEXT;
    const layoutSettings = createLayoutPatch(section.layoutSettings, isText ? LAYOUT_TYPES.TEXT_DEFAULT : LAYOUT_TYPES.MEDIA_FULL_WIDTH);
    layoutSettings.modules = layoutSettings.modules.map(module => module.type === remaining.type
        ? {...module, moduleId: remaining.moduleId} : module);
    if (isText) {
        contentModules[0] = {...remaining, text: {...remaining.text, style: {...remaining.text.style, textAlign: 'left'}}};
    }
    return {
        ...section,
        contentModules,
        styleSettings: {...section.styleSettings, boxAlign: 'left'},
        contentSettings: {
            ...section.contentSettings,
            showText: contentModules.some(module => module.type === SectionModuleTypeEnum.TEXT),
            showMedia: contentModules.some(module => module.type === SectionModuleTypeEnum.MEDIA),
        },
        layoutSettings,
    };
}

// Read-only adapter for the existing public renderers; never used for saving.
export function getModuleRenderSection(section, module) {
    if (section.type !== SectionTypesEnum.GENERAL) return section;
    const content = getModuleContent(section, module.moduleId);
    return {
        ...section,
        text: content?.text,
        media: getModuleMedia(section, module.moduleId),
        cta: content?.cta,
    };
}
